import { Flex, Input, Table } from 'antd';
import SharedDatePicker from '../DatePicker';
import dayjs from 'dayjs';
import { IPODetailTableProps } from 'src/models/PODetailTable';
import type { TableProps } from 'antd';
import { ScrollableContent } from '../ScrollableContent';
import en from 'src/messages/en.json';

const columns: TableProps<any>['columns'] = [
  {
    title: en.POs.Title.poNumber,
    dataIndex: 'purchaseOrderNumber',
    key: 'purchaseOrderNumber',
    width: '15%',
    fixed: 'left',
    sorter: true,
  },
  {
    title: en.POs.Title.vendorName,
    dataIndex: 'vendorName',
    key: 'vendorName',
    ellipsis: true,
    render: (vendorName: string[]) =>
      Array.isArray(vendorName) && vendorName.length
        ? vendorName.join(', ')
        : '',
    sorter: true,
  },
  {
    title: en.POs.Title.costCenter,
    dataIndex: 'costCenterName',
    key: 'costCenterName',
    ellipsis: true,
    render: (costCenterName: string[]) =>
      Array.isArray(costCenterName) && costCenterName.length
        ? costCenterName.join(', ')
        : '',
    sorter: true,
  },
  {
    title: en.POs.Title.issueDate,
    dataIndex: 'orderedOnDate',
    key: 'orderedOnDate',
    ellipsis: true,
    render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
    sorter: true,
  },
  {
    title: en.POs.Title.poDescription,
    dataIndex: 'poDescription',
    key: 'poDescription',
    ellipsis: true,
    sorter: true,
  },
];

const PODetailTable = ({
  data,
  setSelectedPO,
  searchValue,
  searchValueByVendor,
  startDate,
  endDate,
  setSearchValue,
  setSearchValueByVendor,
  handleRangeDateChange,
  isPOTableLoading,
  setIsSecondPanelVisible,
  selectedPO,
  pageNumber,
  setPageNumber,
  pageSize,
  setPageSize,
  totalCount,
  setSortField,
  setSortDirection,
  setTotalCount,
  sortField,
  sortDirection,
}: IPODetailTableProps) => {
  const handleDateChange = (value: any) => {
    if (!value || !Array.isArray(value)) {
      handleRangeDateChange(null, null);

      return;
    }

    const [start, end] = value;
    const startDate = start ? start.toDate() : null;
    const endDate = end ? end.toDate() : null;

    handleRangeDateChange(startDate, endDate);
  };

  return (
    <div style={{ padding: '0.25rem 1rem 0.25rem 0.25rem' }}>
      <Flex
        align="middle"
        justify="space-between"
        style={{ padding: ' 0 0 0.75rem 0' }}
        gap={'middle'}
      >
        <Input
          placeholder={en.POs.Placeholder.poNumber}
          size="middle"
          style={{ width: '33%' }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear
        />
        <Input
          placeholder={en.POs.Placeholder.vendorName}
          size="middle"
          style={{ width: '33%' }}
          value={searchValueByVendor}
          onChange={(e) => setSearchValueByVendor(e.target.value)}
          allowClear
        />
        <div style={{ width: '33%' }}>
          <SharedDatePicker
            isRange={true}
            onChange={handleDateChange}
            startDate={startDate}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                handleRangeDateChange(null, null);
              }
            }}
            endDate={endDate}
            style={{ width: '100%' }}
          />
        </div>
      </Flex>
      <ScrollableContent
        minHeight={'calc(100vh - 12rem)'}
        maxHeight={'calc(100vh - 12rem)'}
      >
        <Table
          dataSource={data}
          columns={columns}
          rowKey="purchaseOrderNumber"
          sticky
          onRow={(record) => ({
            onClick: () => {
              setSelectedPO(record.purchaseOrderNumber);
              setIsSecondPanelVisible(true);
            },
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: pageNumber,
            pageSize,
            total: totalCount,
            onChange: (page, size) => {
              setPageNumber(page);
              setPageSize(size);
            },
            position: ['bottomCenter'],
          }}
          rowClassName={(record) =>
            record.purchaseOrderNumber === selectedPO ? 'selected-po-row' : ''
          }
          scroll={{ x: 800 }}
          loading={isPOTableLoading}
          onChange={(pagination, filters, sorter) => {
            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

            setSortField((singleSorter?.field as string) || '');
            setSortDirection(
              singleSorter?.order === 'descend' ? 'desc' : 'asc',
            );
          }}
          bordered={false}
          size="small"
        />
      </ScrollableContent>
    </div>
  );
};

export default PODetailTable;
